import { makeFieldStyleSelector } from "@client/content/modules/selectors";
import { makeHeadingPropsSelector } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { getNoImageUrl } from "@client/lib/assets";
import { getMultiselectValue } from "@client/lib/settings";
import {
  TargetSelector,
  combinedSettingsSelector,
  getColorSelector,
  getColorWithDefaultSelector,
  makeTextStyleSelector
} from "@client/selectors";
import { type AppState, type EntityRef, type WithId, type WithParentId } from "@client/types";
import { connect } from "react-redux";
import { addToCart } from "../../actions";
import { localStateSelector } from "../../selectors";
import Component, { type ActionProps, type MapProps } from "./component";
import { makeContentStyleSelector, makeImageContainerStyleSelector, makeStyleSelector } from "./selectors";

export type ProductListItemContainerProps = Omit<RootContainerProps, "id"> & WithParentId & WithId<string>;

const makeMapStateToProps = () => {
  // TODO: TS issues
  const fieldStyleSelector = makeFieldStyleSelector();
  const imageContainerStyleSelector = makeImageContainerStyleSelector() as any;
  const styleSelector = makeStyleSelector();
  const contentStyleSelector = makeContentStyleSelector();
  const nameHeadingPropsSelector = makeHeadingPropsSelector(TargetSelector.Entity, "name");
  const quantityStyleSelector = makeTextStyleSelector(TargetSelector.Entity, "quantity");
  const regularPriceStyleSelector = makeTextStyleSelector(TargetSelector.Entity, "price.regular");
  const salePriceStyleSelector = makeTextStyleSelector(TargetSelector.Entity, "price.sale");
  const outOfStockStyleSelector = makeTextStyleSelector(TargetSelector.Entity, "outOfStock");

  const settings = combinedSettingsSelector(TargetSelector.Entity, [
    { name: "buttons", converter: getMultiselectValue, prop: "buttons" },
    { name: "elements", converter: getMultiselectValue, prop: "elements" }
  ]);
  const regularPriceColorSelector = getColorSelector(TargetSelector.Entity, "price.regular.text.color");
  const oldPriceColorSelector = getColorWithDefaultSelector(
    TargetSelector.Entity,
    "price.sale.oldPriceColor",
    regularPriceColorSelector
  );
  const captionsSelector = combinedSettingsSelector<{ add: string; buy: string; detail: string }>(
    TargetSelector.Entity,
    [
      { name: "addToCartCaption", prop: "add" },
      { name: "buyNowCaption", prop: "buy" },
      { name: "seeDetailsCaption", prop: "detail" }
    ]
  );

  return (state: AppState, props: ProductListItemContainerProps): MapProps => {
    const selectorProps: EntityRef = { id: props.parentId, source: props.source };
    const localState = localStateSelector(state, selectorProps);

    const captions = captionsSelector(state, { id: props.parentId, source: props.source });
    const product = localState.items.find((p) => p.id === props.id)!;

    return {
      ...props,
      ...settings(state, selectorProps),
      addCaption: captions.add,
      buyCaption: captions.buy,
      cartUrl: localState.cartUrl,
      contentStyle: contentStyleSelector(state, selectorProps),
      detailCaption: captions.detail,
      fieldStyle: fieldStyleSelector(state, selectorProps),
      imageContainerStyle: imageContainerStyleSelector(state, selectorProps),
      isAddingToCart: localState.status === "AddingToCart",
      nameHeading: nameHeadingPropsSelector(state, selectorProps),
      noImageUrl: getNoImageUrl(),
      oldPriceColor: oldPriceColorSelector(state, selectorProps),
      outOfStockStyle: outOfStockStyleSelector(state, selectorProps),
      product,
      quantityStyle: quantityStyleSelector(state, selectorProps),
      regularPriceStyle: regularPriceStyleSelector(state, selectorProps),
      salePriceStyle: salePriceStyleSelector(state, selectorProps),
      shouldDisplayTax: localState.shouldDisplayTax,
      style: styleSelector(state, selectorProps)
    };
  };
};

const mapDispatchToProps = (dispatch: any, props: ProductListItemContainerProps): ActionProps => ({
  addToCart: (quantity: number) =>
    dispatch(
      addToCart({ id: props.parentId, source: props.source, productId: props.id, quantity, goToCheckout: false })
    ),
  buyNow: (quantity: number) =>
    dispatch(addToCart({ id: props.parentId, source: props.source, productId: props.id, quantity, goToCheckout: true }))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(Component);
